import React, { useState, useMemo, useRef, useCallback } from 'react';
import styled from 'styled-components';
import StyledDropDown from '../StyledDropDown';
import ShowMarksControl from '../ShowMarksControl';
import { updateSubjectSettings, profileStateYear } from '../../service';
import { toast } from 'react-toastify';
import Toast from '../../components/Toast';
import { useProfile, useShortAnswers, useShortAnswersForStudents } from '../../components/state/hooks';
import { isShortAnswerType, isExtendedResponseType, normalizeType, MARKS_OUT_OF_MAX } from '../../components/CreateNewTask/utils/constants';
import { useUserStates } from '../../hooks/useUserStates';
import {
  useApiSubjects,
  useClassSubjects,
  useSubjects,
  useYearsByState,
  useAvailableStates,
  useTaskTypes,
  useResponseTypes,
} from '../../hooks/useSubjects';
import Loader from '../../components/Loader';
import DraftSettingsBackground from '../../static/icons/draft-settings-background.svg';
import EditPurpleIcon from '../../static/icons/edit-purple-icon.svg';
import { isNonSchoolStudentRole, isNonSchoolTeacherRole, isSchoolStudentRole, isSchoolTeacherRole } from '../../utils/userAccessHelper';
import { getUserId, getUserRole } from '../../userLocalDetails';
import { t as lexiconT, isUsCountry, isNzCountry, yearLabel as yearDisplayLabel } from '../../utils/lexicon';

// JED-1004: alias-aware match between a stored task/response-type value and a
// dropdown option. Used for BOTH dedupe-on-append and selection-on-init so a
// legacy alias (e.g. LONG_ANSWER) resolves to its canonical option ("Extended
// Response") instead of falling back to the first option.
const matchesTypeOption = (opt, candidate) => {
  if (opt == null || candidate == null) return false;
  if (
    opt.value === candidate ||
    opt.title === candidate ||
    String(opt.value) === String(candidate)
  )
    return true;
  if (
    normalizeType(opt.value) === normalizeType(candidate) ||
    normalizeType(opt.title) === normalizeType(candidate)
  )
    return true;
  if (
    isExtendedResponseType(candidate) &&
    (isExtendedResponseType(opt.value) || isExtendedResponseType(opt.title))
  )
    return true;
  if (
    isShortAnswerType(candidate) &&
    (isShortAnswerType(opt.value) || isShortAnswerType(opt.title))
  )
    return true;
  return false;
};

const LAST_DRAFT_SETTINGS_KEY = 'jeddle_last_draft_settings';

// Remembered settings are scoped per user so accounts sharing a browser (common
// on school machines) don't inherit each other's state/year/subject. The legacy
// unscoped key is intentionally never read OR written — it can't be attributed
// to a user, so touching it would hand user A's settings to user B on a shared
// machine (the very leak the scoping fixes). With no user id available the
// feature is simply skipped; existing users see profile defaults once, then
// their next create/update writes the scoped key.
const lastDraftSettingsKey = () => {
  let userId = null;
  try {
    userId = getUserId();
  } catch (e) {
    userId = null;
  }
  return userId ? `${LAST_DRAFT_SETTINGS_KEY}.${userId}` : null;
};

const saveLastDraftSettings = (settings) => {
  try {
    const key = lastDraftSettingsKey();
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(settings));
    // Tidy up the unattributable legacy key so it can't linger on shared machines.
    localStorage.removeItem(LAST_DRAFT_SETTINGS_KEY);
  } catch (e) { /* ignore */ }
};

const getLastDraftSettings = () => {
  try {
    const key = lastDraftSettingsKey();
    if (!key) return null;
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Helpers below let us match a Short Answer marking-criteria entry against the
// other filter values selected in Draft Settings, so the warning's "Valid max
// marks" list reflects only SAs the teacher could actually attach here.
const normalizeYearNumber = (val) => {
  const num = parseInt(String(val ?? '').replace(/^Year[\s-]*/i, '').trim(), 10);
  return Number.isFinite(num) ? num : null;
};

const normalizeStateCode = (val) => String(val ?? '').toUpperCase().trim();

const subjectCodeOf = (s) => (typeof s === 'object' && s !== null ? (s.subjectCode ?? s.code) : s);
const taskTypeCodeOf = (t) => (typeof t === 'object' && t !== null ? (t.value ?? t.code) : t);
const responseTypeCodeOf = (r) => (typeof r === 'object' && r !== null ? (r.value ?? r.code) : r);

// An empty/missing filter array on an SA means "applies to all" for that axis.
const matchesArrayFilter = (arr, predicate) =>
  !Array.isArray(arr) || arr.length === 0 || arr.some(predicate);

const matchesState = (states, state) =>
  !state || matchesArrayFilter(states, (s) => normalizeStateCode(s) === normalizeStateCode(state));

const matchesYear = (years, yearNumber) =>
  yearNumber == null || matchesArrayFilter(years, (y) => normalizeYearNumber(y) === yearNumber);

const matchesSubject = (subjects, subjectCode) =>
  !subjectCode || matchesArrayFilter(subjects, (s) => subjectCodeOf(s) === subjectCode);

const matchesTaskType = (taskTypes, taskType) =>
  !taskType || matchesArrayFilter(taskTypes, (t) => taskTypeCodeOf(t) === taskType);

const matchesResponseType = (responseTypes, responseType) =>
  !responseType || matchesArrayFilter(responseTypes, (r) => responseTypeCodeOf(r) === responseType);

const saFilterArrays = (sa) => {
  const meta = sa?.metadata || {};
  return {
    states: sa?.states ?? meta.states ?? [],
    years: sa?.years ?? meta.years ?? [],
    subjects: sa?.subjects ?? meta.subjects ?? [],
    taskTypes: sa?.taskTypes ?? meta.taskTypes ?? [],
    responseTypes: sa?.responseTypes ?? meta.responseTypes ?? [],
  };
};

const shortAnswerMatchesContext = (sa, ctx) => {
  if (!sa) return false;
  if (sa.isUniversal === true) return true;
  const f = saFilterArrays(sa);
  return matchesState(f.states, ctx.state)
    && matchesYear(f.years, ctx.yearNumber)
    && matchesSubject(f.subjects, ctx.subjectCode)
    && matchesTaskType(f.taskTypes, ctx.taskType)
    && matchesResponseType(f.responseTypes, ctx.responseType);
};

const PopupDialogueBox = ({
  open,
  close,
  submission,
  setSubmission,
  mode = 'update',
  onCreateDraft,
  onSettingsUpdated,
  onSubmittingChange,
  hideClose = false,
  // Where Update writes. A submission-backed draft patches the submission, which is
  // every caller today. A bulk draft has no single submission to patch — pre-commit it
  // has none and post-commit it has many — so /drafts/:assignmentId passes the
  // assignment-scoped endpoint instead.
  saveSettings,
}) => {
  const { data: profile, isLoading: isLoadingProfile, resetData: resetProfile } = useProfile();
  const { states: userStates } = useUserStates();
  const isCreateMode = mode === 'create';
  // NSW DoE deliberately removes marks from every question type. Draft settings
  // must follow the same policy as the mobile authoring screens: a Short Answer
  // remains selectable, but there is no impossible marks requirement and no mark
  // is sent back from a stale/legacy draft.
  const isNswDoe = profile?.isNSWDOE === true;
  const isNonSchoolUser = isNonSchoolStudentRole(profile) || isNonSchoolTeacherRole(profile);
  const isSchoolStudent = isSchoolStudentRole(profile);
  const isSchoolTeacher = isSchoolTeacherRole(profile);
  const isSchoolUser = isSchoolTeacher || isSchoolStudent;
  const hasMultipleProfileStates = userStates.length > 1;
  // NZ has one sentinel state "NZ" with no curriculum subdivisions — hide the
  // State selector everywhere (dropdown + the "State: NZ" compact label) and
  // fall back to a year-only compact summary for non-school NZ users. AU/US
  // paths are untouched (isNzUser is false for them).
  const isNzUser = isNzCountry(profile?.country);
  const hideState = (isSchoolUser && !hasMultipleProfileStates) || isNzUser;
  const showCompactStateYear = !hideState && !isSchoolUser && (isNonSchoolUser || isCreateMode);
  const showCompactYearOnly =
    (isSchoolStudent && hideState) ||
    (isNzUser && !isSchoolUser && (isNonSchoolUser || isCreateMode));
  const showCompact = !isCreateMode && (showCompactStateYear || showCompactYearOnly);
  const [isEditingStateYear, setIsEditingStateYear] = useState(false);

  const currentState = userStates[0] || null;
  const currentYear = profile?.year;

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedTaskType, setSelectedTaskType] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedResponseType, setSelectedResponseType] = useState(null);
  const [marksOutOf, setMarksOutOf] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasUserChangedSubjectRef = useRef(false);
  const hasUserChangedTaskTypeRef = useRef(false);
  const hasUserChangedStateRef = useRef(false);
  const hasUserChangedYearRef = useRef(false);
  const hasUserChangedMarksRef = useRef(false);

  const { subjects: subjectsData, loading: loadingSubjects } = useClassSubjects();
  const { subjects: subjectsByState, loading: loadingSubjectsByState } = useSubjects(currentState, currentYear);
  const { states: statesData, loading: loadingStates } = useAvailableStates();
  // Source the Short Answer library by role. useShortAnswers() hits /teachers and
  // returns [] for non-teachers (service gates on isTeacher()), so a student saw
  // the SA-feedback warning below fire for every mark. Students read their own
  // class-scoped SAs (/students/shortAnswers) — same shape, same role split used
  // by SelectReviewType when it actually applies SA feedback.
  const { data: teacherShortAnswersData } = useShortAnswers();
  const { data: studentShortAnswersData } = useShortAnswersForStudents();
  const shortAnswersData = getUserRole() === 'TEACHER' ? teacherShortAnswersData : studentShortAnswersData;

  // Track if we've initialized the values for this submission
  const initializedRef = useRef(false);
  const submissionIdRef = useRef(submission?.id);

  const assign = submission?.assignment;
  const firstQuestion = assign?.questions?.[0];

  const toStr = (v) => {
    if (v == null) return '';
    if (typeof v !== 'object') return String(v);
    const x = v.title ?? v.label ?? v.name ?? v.subjectName ?? v.code ?? v.value ?? '';
    return typeof x === 'string' ? x : toStr(x);
  };

  const getSubmissionState = () => submission?.state ?? userStates[0] ?? null;

  const getSubmissionYear = () => {
    const year = (Array.isArray(firstQuestion?.years) ? firstQuestion.years[0] : firstQuestion?.years)
      ?? (Array.isArray(assign?.years) ? assign.years[0] : assign?.years)
      ?? submission?.year;
    if (year == null) return null;
    const s = String(year).trim();
    if (!s) return null;
    if (s.toLowerCase().startsWith('year')) return s;
    const num = s.replace(/^Year-?\s*/i, '').trim() || s;
    return num.match(/^\d+$/) ? `Year ${num}` : s;
  };

  const getSubmissionSubjectCode = () => {
    const subjects = firstQuestion?.subjects ?? submission?.assignment?.subjects;
    if (Array.isArray(subjects) && subjects.length > 0) {
      const firstSubject = subjects[0];
      if (typeof firstSubject === 'object') {
        return firstSubject.subjectCode || firstSubject.code || '';
      }
      return String(firstSubject);
    }
    const raw = submission?.effectiveSubjectName ?? submission?.subjectName ?? assign?.subjectName ?? (Array.isArray(assign?.subjects) ? assign.subjects[0] : assign?.subjects);
    const v = raw != null && typeof raw === 'object' ? (raw.subjectCode ?? raw.code ?? '') : raw;
    return v != null ? String(toStr(v)) : (typeof raw === 'string' ? raw : '');
  };

  const getSubmissionSubject = () => {
    const subjectCode = getSubmissionSubjectCode();
    const match = subjectOptions.find((s) => s.code === subjectCode);
    return match?.title || subjectCode || '';
  };

  const getSubmissionTaskType = () => {
    const raw = (firstQuestion?.taskType != null && typeof firstQuestion.taskType === 'object'
      ? firstQuestion.taskType?.value ?? firstQuestion.taskType?.label ?? firstQuestion.taskType
      : firstQuestion?.taskType)
      ?? (assign?.taskType != null && typeof assign.taskType === 'object' ? assign.taskType?.value ?? assign.taskType?.label : assign?.taskType)
      ?? submission?.effectiveTaskType ?? submission?.taskType ?? submission?.documentType;
    const v = raw != null && typeof raw === 'object' ? (raw.value ?? raw.code ?? raw.label ?? raw.name ?? '') : raw;
    return v != null ? String(toStr(v)) : (typeof raw === 'string' ? raw : '');
  };

  const getSubmissionResponseType = () => {
    const raw = (firstQuestion?.responseType != null && typeof firstQuestion?.responseType === 'object'
      ? firstQuestion.responseType?.value ?? firstQuestion.responseType?.label ?? firstQuestion.responseType
      : firstQuestion?.responseType)
      ?? (assign?.questions?.[0]?.responseType != null && typeof assign.questions[0].responseType === 'object'
        ? assign.questions[0].responseType?.value ?? assign.questions[0].responseType?.label
        : assign?.questions?.[0]?.responseType)
      ?? submission?.effectiveResponseType ?? submission?.responseType;
    const v = raw != null && typeof raw === 'object' ? (raw.value ?? raw.label ?? '') : raw;
    return v != null ? String(toStr(v)) : (typeof raw === 'string' ? raw : '');
  };

  const getSubmissionTotalMarks = () => {
    const raw = submission?.totalMarks ?? assign?.totalMarks ?? firstQuestion?.totalMarks ?? firstQuestion?.marksOutOf;
    return raw != null && String(raw).trim() ? String(raw).trim() : '';
  };

  const rawStates = statesData ?? [];
  const stateOptions = useMemo(() => {
    const list = rawStates.map((s, i) => {
      const code = typeof s === 'string' ? s : ((s?.code ?? s?.state ?? toStr(s)) || '');
      const title = code;
      return { id: title || `state-${i}`, title: String(title), code: String(code || '') };
    });
    if (!isCreateMode && submission) {
      const stateVal = getSubmissionState();
      if (stateVal && !list.some((opt) => opt.title === String(stateVal))) {
        list.push({ id: stateVal, title: String(stateVal), code: String(stateVal) });
      }
    }
    if (list.length === 0) {
      if (userStates.length > 0) {
        userStates.forEach((s) => {
          const code = String(s);
          if (!list.some((opt) => opt.title === code)) {
            list.push({ id: code, title: code, code });
          }
        });
      } else if (currentState) {
        list.push({ id: currentState, title: String(currentState), code: String(currentState) });
      }
    }
    return list;
  }, [rawStates, isCreateMode, submission, userStates, currentState]);

  const resolvedStateOption = useMemo(() => {
    const selectedStateTitle = selectedState?.title ? String(selectedState.title) : null;
    if (selectedStateTitle) {
      return stateOptions.find((opt) => opt.title === selectedStateTitle) ?? selectedState;
    }

    const submissionState = getSubmissionState();
    return stateOptions.find((opt) => opt.title === String(submissionState))
      ?? stateOptions.find((opt) => opt.title === String(currentState))
      ?? stateOptions[0]
      ?? null;
  }, [selectedState, stateOptions, submission?.state, currentState, userStates]);

  // State used for filtering years - updates when user selects a different state
  const stateForYears = resolvedStateOption?.title ?? submission?.state ?? currentState;
  const { years: yearsData, loading: loadingYears } = useYearsByState(stateForYears);

  const effectiveState = (resolvedStateOption?.title ?? '').toString().trim();
  const effectiveYear = selectedYear?.title ?? getSubmissionYear() ?? (currentYear != null ? `Year ${currentYear}` : undefined);
  const { apiSubjects: rawApiSubjects, loading: loadingSubjectsByEffective } = useApiSubjects();

  // Returns true if at least one API subject matches the given year under the effective state.
  // Used to avoid sticky-selecting a year that would yield empty subjectOptions (stuck modal).
  const subjectsAvailableForYear = useCallback((yearTitle) => {
    if (!rawApiSubjects?.length) return false;
    const stateNorm = effectiveState ? String(effectiveState).toUpperCase().trim() : '';
    const yearNum = parseInt(String(yearTitle || '').replace(/^Year-?\s*/i, '').trim(), 10);
    if (Number.isNaN(yearNum)) return false;
    return rawApiSubjects.some((s) => {
      const stateOk = !stateNorm || !s.states?.length
        || s.states.some((st) => String(st).toUpperCase() === stateNorm);
      const yearOk = !s.years?.length || s.years.some((y) => Number(y) === yearNum);
      return stateOk && yearOk;
    });
  }, [rawApiSubjects, effectiveState]);

  const hasStateAndYearFilter = useMemo(() => {
    const stateNorm = effectiveState ? String(effectiveState).toUpperCase().trim() : '';
    const yearStr = effectiveYear != null ? String(effectiveYear).trim() : '';
    const yearNum = yearStr ? parseInt(yearStr.replace(/^Year-?\s*/i, '').trim(), 10) : null;
    const yearNumValid = !Number.isNaN(yearNum) ? yearNum : null;
    return !!(stateNorm && yearNumValid != null);
  }, [effectiveState, effectiveYear]);

  const subjectOptions = useMemo(() => {
    const stateNorm = effectiveState ? String(effectiveState).toUpperCase().trim() : '';
    const yearStr = effectiveYear != null ? String(effectiveYear).trim() : '';
    const yearNum = yearStr ? parseInt(yearStr.replace(/^Year-?\s*/i, '').trim(), 10) : null;
    const yearNumValid = !Number.isNaN(yearNum) ? yearNum : null;
    const matchesYear = (yearsArr) =>
      yearNumValid == null || !Array.isArray(yearsArr) || !yearsArr.length ||
      yearsArr.some((y) => Number(y) === yearNumValid || y === yearNumValid);
    const matchesState = (s) =>
      !stateNorm || !s.states?.length || s.states.some((st) => String(st).toUpperCase() === stateNorm);
    if (isCreateMode) {
      const source = rawApiSubjects?.length ? rawApiSubjects : (subjectsData || []);
      const filtered = hasStateAndYearFilter
        ? source.filter((s) => matchesState(s) && matchesYear(s.years))
        : source;
      return filtered.map((s) => {
        const title = toStr(s?.title ?? s?.subjectName ?? s?.name ?? s?.code) || '';
        const code = s?.subjectCode ?? s?.code ?? title;
        return { id: code || title, title: String(title), code };
      });
    }
    const filtered =
      hasStateAndYearFilter && rawApiSubjects?.length
        ? rawApiSubjects.filter((s) => matchesState(s) && matchesYear(s.years))
        : [];
    const list = filtered.map((s) => {
      const title = toStr(s?.title ?? s?.subjectName ?? s?.name ?? s?.code) ?? (s?.subjectCode ?? s?.code) ?? '';
      const code = s?.subjectCode ?? s?.code;
      return { id: code || title, title: String(title), code };
    });
    if (submission) {
      const subjects = firstQuestion?.subjects ?? submission?.assignment?.subjects;
      if (Array.isArray(subjects) && subjects.length > 0) {
        const firstSubject = subjects[0];
        const subjectName = typeof firstSubject === 'object'
          ? (firstSubject.subjectName ?? firstSubject.name ?? firstSubject.title ?? firstSubject.subjectCode ?? firstSubject.code)
          : String(firstSubject);
        const subjectCode = typeof firstSubject === 'object'
          ? (firstSubject.subjectCode ?? firstSubject.code)
          : firstSubject;
        if (
          subjectName &&
          !list.some((s) => s.code === subjectCode || s.id === subjectCode || s.title === subjectName)
        ) {
          list.push({ id: subjectCode || subjectName, title: String(subjectName), code: subjectCode || subjectName });
        }
      } else {
        const subjectCode = getSubmissionSubjectCode();
        if (subjectCode && !list.some((s) => s.title === subjectCode || s.code === subjectCode)) {
          list.push({ id: subjectCode, title: subjectCode, code: subjectCode });
        }
      }
    }
    return list;
  }, [isCreateMode, subjectsData, rawApiSubjects, submission, firstQuestion, effectiveState, effectiveYear]);

  // Disambiguate subject display titles when names collide
  const disambiguatedSubjectOptions = useMemo(() => {
    const titleCounts = {};
    subjectOptions.forEach((s) => {
      titleCounts[s.title] = (titleCounts[s.title] || 0) + 1;
    });
    return subjectOptions.map((s) => {
      if (titleCounts[s.title] > 1 && s.code && s.code !== s.title) {
        return { ...s, title: `${s.title} (${s.code})` };
      }
      return s;
    });
  }, [subjectOptions]);

  const yearMatch = (a, b) => {
    const na = (a || '').replace(/^Year-?\s*/i, '').trim();
    const nb = (b || '').replace(/^Year-?\s*/i, '').trim();
    return na && nb && na === nb;
  };

  const rawYears = yearsData ?? [];
  const yearOptions = useMemo(() => {
    const list = rawYears.map((y, i) => {
      const title = typeof y === 'number' ? `Year ${y}` : String(toStr(y) || '');
      return { id: title || `year-${i}`, title };
    });
    if (!isCreateMode && submission) {
      const currentYearStr = getSubmissionYear();
      if (currentYearStr && !list.some((y) => y.title === currentYearStr || yearMatch(y.title, currentYearStr))) {
        list.push({ id: currentYearStr, title: currentYearStr });
      }
    }
    // Fallback: add profile year if list is empty
    if (list.length === 0 && currentYear) {
      const yearTitle = `Year ${currentYear}`;
      list.push({ id: yearTitle, title: yearTitle });
    }
    // US display only: titles stay "Year N" (they're matching keys and the
    // persisted wire format); label is what StyledDropDown renders.
    if (isUsCountry(profile?.country)) {
      return list.map((o) =>
        /^Year\s*-?\s*\d+$/i.test(o.title)
          ? { ...o, displayTitle: yearDisplayLabel(o.title, profile?.country) }
          : o
      );
    }
    return list;
  }, [rawYears, isCreateMode, submission, firstQuestion, currentYear, profile?.country]);

  const currentSubjectForFilter = useMemo(() => {
    if (selectedSubject) return selectedSubject.code || selectedSubject.title;
    const currentCode = getSubmissionSubjectCode();
    if (currentCode) return currentCode;
    return subjectsData[0]?.code || subjectsData[0]?.title || '';
  }, [selectedSubject, subjectsData, submission]);

  const currentTaskTypeForFilter = selectedTaskType?.value ?? selectedTaskType?.title ?? (hasUserChangedSubjectRef.current ? null : getSubmissionTaskType()) ?? null;

  const { taskTypes: taskTypesData, loading: loadingTaskTypes } = useTaskTypes(
    currentSubjectForFilter ? [currentSubjectForFilter] : [],
    effectiveState,
    effectiveYear
  );
  const { responseTypes: responseTypesData, loading: loadingResponseTypes } = useResponseTypes(
    currentSubjectForFilter ? [currentSubjectForFilter] : [],
    currentTaskTypeForFilter,
    effectiveState,
    effectiveYear
  );

  const taskTypeOptions = useMemo(() => {
    const currentTaskType = getSubmissionTaskType();
    if (!isCreateMode && !hasStateAndYearFilter) {
      return currentTaskType
        ? [{ id: currentTaskType, title: String(currentTaskType), value: currentTaskType }]
        : [];
    }
    const source = taskTypesData ?? [];
    const options = source.map((t, i) => {
      const title = toStr(t?.label ?? t?.name ?? t?.title ?? t?.value) || '';
      const value = t?.value ?? t?.code;
      return { id: value ?? `task-${i}`, title: String(title), value };
    });
    // Only append submission's task type as fallback during initial load, not after user changed subject.
    // Alias-aware: a stored legacy value maps onto its existing canonical option instead of
    // being appended as a separate raw dropdown entry.
    if (!hasUserChangedSubjectRef.current && currentTaskType && !options.some(o => matchesTypeOption(o, currentTaskType))) {
      options.push({ id: currentTaskType, title: String(currentTaskType), value: currentTaskType });
    }
    return options;
  }, [isCreateMode, hasStateAndYearFilter, taskTypesData, submission, firstQuestion]);

  const responseTypeOptions = useMemo(() => {
    const currentResponseType = getSubmissionResponseType();
    if (!isCreateMode && !hasStateAndYearFilter) {
      return currentResponseType
        ? [{ id: String(currentResponseType), title: String(currentResponseType), value: currentResponseType }]
        : [];
    }
    const source = responseTypesData ?? [];
    const options = source.map((r, i) => {
      const title = toStr(r?.label ?? r?.value ?? r) || '';
      const value = r?.value ?? r?.code ?? r;
      return { id: value ?? `response-${i}`, title: String(title), value };
    });
    // Only append submission's response type as fallback during initial load, not after user changed subject.
    // Alias-aware: a stored legacy value (e.g. LONG_ANSWER) maps onto the existing canonical
    // "Extended Response" option instead of being appended as a separate raw dropdown entry.
    if (!hasUserChangedSubjectRef.current && !hasUserChangedTaskTypeRef.current && currentResponseType && !options.some(o => matchesTypeOption(o, currentResponseType))) {
      options.push({ id: String(currentResponseType), title: String(currentResponseType), value: currentResponseType });
    }
    return options;
  }, [isCreateMode, hasStateAndYearFilter, responseTypesData, submission, firstQuestion]);

  // Real catalogs use both shapes: Short Answer can be the task type itself, or
  // a response under a broader task. Only the task-level shape has no response
  // dropdown; both shapes require marks outside NSW DoE.
  const selectedTaskTypeValue = selectedTaskType?.value ?? selectedTaskType?.title ?? '';
  const selectedResponseTypeValue =
    selectedResponseType?.value ?? selectedResponseType?.title ?? '';
  const isShortAnswerTaskSelected = isShortAnswerType(selectedTaskTypeValue);
  const isShortAnswerSelected =
    isShortAnswerTaskSelected || isShortAnswerType(selectedResponseTypeValue);
  const hasCatalogContextChanged =
    hasUserChangedStateRef.current ||
    hasUserChangedYearRef.current ||
    hasUserChangedSubjectRef.current ||
    hasUserChangedTaskTypeRef.current;
  const selectedTaskIsCurrent = taskTypeOptions.some((option) =>
    matchesTypeOption(option, selectedTaskTypeValue)
  );
  const selectedResponseIsCurrent = responseTypeOptions.some((option) =>
    matchesTypeOption(option, selectedResponseTypeValue)
  );
  // An upstream selection clears the dependent fields. Keep Update disabled for
  // the render(s) between that clear and the catalog effects choosing valid
  // defaults. This also prevents a cached response from the previous task from
  // being serialized during a fast click.
  const isCatalogCascadeSettling =
    hasCatalogContextChanged &&
    (loadingTaskTypes ||
      loadingResponseTypes ||
      (taskTypeOptions.length > 0 && !selectedTaskIsCurrent) ||
      (!isShortAnswerTaskSelected &&
        responseTypeOptions.length > 0 &&
        !selectedResponseIsCurrent));

  // Surfaces a warning when the entered max mark has no Short Answer marking
  // criterion with a matching highestMarks — SA-based feedback can't be applied
  // automatically without one.
  const parseEnteredMarks = () => {
    const parsed = Number(marksOutOf);
    return Number.isInteger(parsed) && parsed > 0 && parsed <= MARKS_OUT_OF_MAX
      ? parsed
      : null;
  };

  const shortAnswerContext = () => ({
    state: effectiveState,
    yearNumber: normalizeYearNumber(effectiveYear),
    subjectCode: currentSubjectForFilter,
    taskType: currentTaskTypeForFilter,
    responseType: responseTypeCodeOf(selectedResponseType) ?? selectedResponseType?.title,
  });

  const matchingShortAnswers = () => {
    const list = Array.isArray(shortAnswersData) ? shortAnswersData : [];
    const ctx = shortAnswerContext();
    return list.filter((sa) => shortAnswerMatchesContext(sa, ctx));
  };

  const availableShortAnswerMarks = () => {
    const marks = matchingShortAnswers()
      .map((sa) => Number(sa?.highestMarks))
      .filter((n) => Number.isFinite(n) && n > 0);
    return Array.from(new Set(marks)).sort((a, b) => a - b);
  };

  // Max mark is required for short-answer drafts in both modes of this popup;
  // the submit button is gated on a positive numeric value.
  const isMarksRequiredAndMissing = () => isShortAnswerSelected && !isNswDoe && parseEnteredMarks() == null;

  const formatValidMarksSuffix = (validMarks) =>
    validMarks.length === 0 ? '' : ` Valid max marks: ${validMarks.join(', ')}.`;

  const formatMarksWarning = (entered, validMarks) =>
    `There is no Short Answer marking criteria with a max mark of ${entered}, ` +
    `so Short Answer feedback can't be applied.` +
    formatValidMarksSuffix(validMarks);

  const buildMarksWarning = () => {
    if (!isShortAnswerSelected || isNswDoe) return '';
    const entered = parseEnteredMarks();
    if (entered == null) return '';
    const validMarks = availableShortAnswerMarks();
    if (validMarks.includes(entered)) return '';
    return formatMarksWarning(entered, validMarks);
  };

  const marksWarning = buildMarksWarning();

  React.useEffect(() => {
    // Reset initialization when popup closes or submission changes
    if (!open) {
      initializedRef.current = false;
      submissionIdRef.current = submission?.id;
      hasUserChangedSubjectRef.current = false;
      hasUserChangedTaskTypeRef.current = false;
      hasUserChangedStateRef.current = false;
      hasUserChangedYearRef.current = false;
      hasUserChangedMarksRef.current = false;
      setMarksOutOf('');
      return;
    }
    if (isCreateMode) return;

    // Skip if already initialized for this submission
    if (initializedRef.current && submissionIdRef.current === submission?.id) return;

    // Only initialize when data is loaded
    const isLoading = loadingSubjects || loadingSubjectsByState || loadingSubjectsByEffective || loadingYears || loadingStates || loadingTaskTypes || loadingResponseTypes;
    if (isLoading) return;

    const currentYearStr = getSubmissionYear();
    const yearIdx = currentYearStr ? yearOptions.findIndex((y) => y.title === currentYearStr || yearMatch(y.title, currentYearStr)) : -1;
    const stateVal = getSubmissionState();
    const stateIdx = stateVal ? stateOptions.findIndex((s) => s.title === String(stateVal)) : -1;

    // Get subject name from stored data for matching (prioritizes name over code)
    const subjects = firstQuestion?.subjects ?? submission?.assignment?.subjects;
    let subjectNameToMatch = null;
    if (Array.isArray(subjects) && subjects.length > 0) {
      const firstSubject = subjects[0];
      subjectNameToMatch = typeof firstSubject === 'object'
        ? (firstSubject.subjectName ?? firstSubject.name ?? firstSubject.title ?? firstSubject.subjectCode ?? firstSubject.code)
        : String(firstSubject);
    }
    const submissionCode = getSubmissionSubjectCode();
    const subjectForMatch = subjectNameToMatch || getSubmissionSubject();
    const subjIdx = disambiguatedSubjectOptions.findIndex((s) =>
      s.code === submissionCode ||
      s.title === subjectForMatch ||
      s.code === subjectForMatch ||
      (subjectForMatch && s.title.toLowerCase() === subjectForMatch.toLowerCase())
    );

    // JED-1004: alias-aware selection so a deduped legacy value still resolves
    // to its canonical option instead of falling back to the first option.
    const taskIdx = taskTypeOptions.findIndex((t) => matchesTypeOption(t, getSubmissionTaskType()));
    const currentResp = getSubmissionResponseType();
    const respIdx = responseTypeOptions.findIndex((r) => matchesTypeOption(r, currentResp));
    setSelectedYear(yearIdx >= 0 ? yearOptions[yearIdx] : (yearOptions[0] ?? null));
    setSelectedState(stateIdx >= 0 ? stateOptions[stateIdx] : null);
    setSelectedSubject(subjIdx >= 0 ? disambiguatedSubjectOptions[subjIdx] : (disambiguatedSubjectOptions[0] ?? null));
    setSelectedTaskType(taskIdx >= 0 ? taskTypeOptions[taskIdx] : (taskTypeOptions[0] ?? null));
    setSelectedResponseType(respIdx >= 0 ? responseTypeOptions[respIdx] : (responseTypeOptions[0] ?? null));

    initializedRef.current = true;
    submissionIdRef.current = submission?.id;
  }, [open, isCreateMode, submission?.id, submission?.year, submission?.state, submission?.effectiveSubjectName, submission?.effectiveTaskType, submission?.effectiveResponseType, firstQuestion?.years, firstQuestion?.subjects, firstQuestion?.taskType, firstQuestion?.responseType, yearOptions, stateOptions, subjectOptions, taskTypeOptions, responseTypeOptions, loadingSubjects, loadingSubjectsByState, loadingSubjectsByEffective, loadingYears, loadingStates, loadingTaskTypes, loadingResponseTypes]);

  // Only reset editing state when popup closes
  React.useEffect(() => {
    if (!open) {
      setIsEditingStateYear(false);
    }
  }, [open]);

  // Compact state/year defaults for update mode — create mode is handled by the main init effect
  React.useEffect(() => {
    if (!open || isCreateMode || !showCompact) return;
    if (!hideState && !hasUserChangedStateRef.current && stateOptions.length > 0) {
      // Update mode edits an existing draft, so the draft's own saved state must
      // win over the profile default — otherwise reopening a draft silently
      // rewrites its state. Fall back to the profile state only when the draft
      // has none.
      const submissionStateVal = getSubmissionState();
      const submissionStateIdx = submissionStateVal
        ? stateOptions.findIndex((s) => s.title === String(submissionStateVal))
        : -1;
      const profileStateIdx = stateOptions.findIndex((s) => s.title === String(currentState));
      const nextState = submissionStateIdx >= 0
        ? stateOptions[submissionStateIdx]
        : (profileStateIdx >= 0 ? stateOptions[profileStateIdx] : null);
      if (nextState && (!selectedState || selectedState.title !== nextState.title)) {
        setSelectedState(nextState ?? null);
      }
    }
    if (!hasUserChangedYearRef.current && yearOptions.length > 0) {
      // Preload the draft's own saved year. Previously this always snapped to the
      // profile year (which defaults to Year 7), so reopening a draft created for
      // a senior year reset it to 7 and then cleared the subject when that subject
      // wasn't offered in Year 7. The draft's year wins; profile is only a
      // fallback for drafts with no saved year.
      const submissionYearStr = getSubmissionYear();
      const submissionYearIdx = submissionYearStr
        ? yearOptions.findIndex((y) => y.title === submissionYearStr || yearMatch(y.title, submissionYearStr))
        : -1;
      const profileYearIdx = yearOptions.findIndex((y) => yearMatch(y.title, currentYear) || y.title === `Year ${currentYear}`);
      const nextYear = submissionYearIdx >= 0
        ? yearOptions[submissionYearIdx]
        : (profileYearIdx >= 0 ? yearOptions[profileYearIdx] : yearOptions[0]);
      if (!selectedYear || !yearMatch(selectedYear.title, nextYear?.title) || selectedYear.title !== nextYear?.title) {
        setSelectedYear(nextYear ?? null);
      }
    }
  }, [open, isCreateMode, showCompact, hideState, currentState, currentYear, stateOptions, yearOptions, selectedState, selectedYear]);

  // Year sync: if selectedYear drops out of yearOptions (e.g. state change shrinks it),
  // pick the first year that has matching subjects so the cascade keeps rendering.
  React.useEffect(() => {
    if (!open) return;
    // The sync effects below are corrective: they repair selections invalidated by a
    // later upstream change. They must not run while the init effects are still
    // applying the remembered/submission settings — these effects are registered
    // first, so they'd fill fields with first-option fallbacks (e.g. Year 7) one
    // commit before init reaches that field, and init (which only fills null
    // fields) would then skip the remembered value entirely.
    if (!initializedRef.current) return;
    if (!yearOptions.length) {
      if (isCreateMode) setSelectedYear(null);
      return;
    }
    const selTitle = selectedYear?.title;
    const inList = selTitle && yearOptions.some((y) => y.title === selTitle || yearMatch(y.title, selTitle));
    if (inList) return;
    // In update mode, when the selection is null/invalid (initial load, or a state
    // change that nulled it), prefer the draft's own saved year if it's a valid
    // option. Without this the first render races the init effects to a first-valid
    // fallback (often Year 7), so a senior-year draft flashes Year 7 for a frame and
    // can trigger a spurious profile.year write before the compact effect corrects it.
    if (!isCreateMode) {
      const submissionYearStr = getSubmissionYear();
      const submissionIdx = submissionYearStr
        ? yearOptions.findIndex((y) => y.title === submissionYearStr || yearMatch(y.title, submissionYearStr))
        : -1;
      if (submissionIdx >= 0 && subjectsAvailableForYear(yearOptions[submissionIdx].title)) {
        setSelectedYear(yearOptions[submissionIdx]);
        return;
      }
    }
    const firstValidIdx = yearOptions.findIndex((y) => subjectsAvailableForYear(y.title));
    setSelectedYear(yearOptions[firstValidIdx >= 0 ? firstValidIdx : 0] ?? null);
  }, [open, isCreateMode, yearOptions, selectedYear, subjectsAvailableForYear]);

  // Subject sync: when year/state change, if selectedSubject is no longer in the filtered
  // options, auto-pick the first one (or the submission's own subject in update mode) so
  // Task Type and Response Type cascades can repopulate.
  React.useEffect(() => {
    if (!open) return;
    if (!initializedRef.current) return;
    if (!disambiguatedSubjectOptions.length) {
      if (isCreateMode) setSelectedSubject(null);
      return;
    }
    const selCode = selectedSubject?.code;
    const selTitle = selectedSubject?.title;
    const inList = selCode
      ? disambiguatedSubjectOptions.some((s) => s.code === selCode)
      : !!selTitle && disambiguatedSubjectOptions.some((s) => s.title === selTitle);
    if (inList) return;
    if (!isCreateMode && submission) {
      const submissionCode = getSubmissionSubjectCode();
      const matchIdx = submissionCode
        ? disambiguatedSubjectOptions.findIndex((s) => s.code === submissionCode)
        : -1;
      if (matchIdx >= 0) {
        setSelectedSubject(disambiguatedSubjectOptions[matchIdx]);
        return;
      }
    }
    setSelectedSubject(disambiguatedSubjectOptions[0] ?? null);
  }, [open, isCreateMode, disambiguatedSubjectOptions, selectedSubject, submission]);

  // Persist selectedYear to profile.year on every change (auto-pick or user-pick). Keeps
  // MongoDB stickiness in sync with the most recent choice even if the modal is dismissed
  // without submitting. Dedup via ref to avoid repeat writes for the same value.
  const lastPersistedYearRef = useRef(null);
  React.useEffect(() => {
    if (!open) return;
    // Never persist mid-initialization auto-picks. Before the init effects settle,
    // selectedYear can transiently hold a cascade fallback (e.g. first-valid Year 7);
    // writing that to profile.year would corrupt the sticky default everywhere else.
    if (!initializedRef.current) return;
    const title = selectedYear?.title;
    if (!title) return;
    const yearNum = parseInt(String(title).replace(/^Year-?\s*/i, '').trim(), 10);
    if (Number.isNaN(yearNum)) return;
    // Check profile first: if it already matches, no write needed. Checking the ref before
    // this would incorrectly skip re-sync when profile.year drifts (external update, or
    // a prior write failed silently) since the ref would still think we persisted yearNum.
    if (profile?.year != null && String(profile.year) === String(yearNum)) return;
    // Ref dedup is strictly for in-flight writes, not a stand-in for profile state.
    if (lastPersistedYearRef.current === yearNum) return;
    lastPersistedYearRef.current = yearNum;
    profileStateYear({ year: yearNum })
      .then(() => resetProfile?.())
      .catch(() => { lastPersistedYearRef.current = null; });
  }, [open, selectedYear, profile?.year]);

  React.useEffect(() => {
    if (!open) return;
    if (!initializedRef.current) return;
    if (isCreateMode && !taskTypeOptions.length) {
      setSelectedTaskType(null);
      return;
    }
    if (!taskTypeOptions.length) return;
    const selVal = selectedTaskType?.value ?? selectedTaskType?.title;
    const inList = selVal && taskTypeOptions.some((t) => (t.value ?? t.title) === selVal);
    if (!inList) {
      if (!isCreateMode && submission && !hasUserChangedTaskTypeRef.current) {
        const current = getSubmissionTaskType();
        // JED-1004: alias-aware so a deduped legacy value resolves to its
        // canonical option instead of overwriting it with option[0].
        const matchIdx = taskTypeOptions.findIndex((t) => matchesTypeOption(t, current));
        if (matchIdx >= 0) {
          setSelectedTaskType(taskTypeOptions[matchIdx]);
          return;
        }
      }
      setSelectedTaskType(taskTypeOptions[0] ?? null);
    }
  }, [open, isCreateMode, taskTypeOptions, submission]);

  React.useEffect(() => {
    if (!open) return;
    if (!initializedRef.current) return;
    if (isCreateMode && !responseTypeOptions.length) {
      setSelectedResponseType(null);
      return;
    }
    if (!responseTypeOptions.length) return;
    const selVal = selectedResponseType?.value ?? selectedResponseType?.title;
    const inList = selVal && responseTypeOptions.some((r) => (r.value ?? r.title) === selVal);
    if (!inList) {
      if (!isCreateMode && submission && !hasUserChangedTaskTypeRef.current) {
        const current = getSubmissionResponseType();
        // JED-1004: alias-aware so a deduped legacy value (e.g. LONG_ANSWER)
        // resolves to its canonical option instead of overwriting with option[0].
        const matchIdx = responseTypeOptions.findIndex((r) => matchesTypeOption(r, current));
        if (matchIdx >= 0) {
          setSelectedResponseType(responseTypeOptions[matchIdx]);
          return;
        }
      }
      setSelectedResponseType(responseTypeOptions[0] ?? null);
    }
  }, [open, isCreateMode, responseTypeOptions, submission]);

  // Pre-fill the max mark: use the submission's existing marks in edit mode,
  // a sensible default for a new short answer draft, otherwise leave blank. NSW
  // DoE never collects marks. Skips once the user interacts so a manual clear
  // isn't reverted.
  React.useEffect(() => {
    if (!open || !isShortAnswerSelected || isNswDoe || hasUserChangedMarksRef.current) return;
    const existing = !isCreateMode ? getSubmissionTotalMarks() : '';
    if (existing) {
      setMarksOutOf(existing);
    } else if (isCreateMode) {
      setMarksOutOf(String(MARKS_OUT_OF_MAX));
    } else {
      setMarksOutOf('');
    }
  }, [open, isShortAnswerSelected, isNswDoe, isCreateMode, submission?.id]);

  // Create mode initialization - use last draft settings if available, otherwise profile defaults
  React.useEffect(() => {
    if (!open || !isCreateMode) return;
    if (initializedRef.current) return;

    const isLoading = isLoadingProfile || loadingSubjects || loadingYears || loadingStates || loadingTaskTypes || loadingResponseTypes;
    if (isLoading) return;
    // Intentionally do NOT bail on empty disambiguatedSubjectOptions here. That list is derived
    // from effectiveState+effectiveYear, which on first render can fall back to a stale profile
    // state/year that yields zero matches. Bailing would leave selectedState/Year unset, so the
    // UI shows sensible defaults via selectedIndex but effectiveState never advances past the
    // stale fallback, and the cascade stays stuck. Let state and year set themselves first;
    // subjectOptions will recompute on the next render and the effect will re-enter.

    const last = getLastDraftSettings();

    // Set in cascade order: state -> year -> subject -> task type -> response type
    if (!selectedState && stateOptions.length > 0) {
      const lastStateIdx = last?.state ? stateOptions.findIndex((s) => s.title === last.state) : -1;
      const profileStateIdx = stateOptions.findIndex((s) => s.title === String(currentState));
      setSelectedState(stateOptions[lastStateIdx >= 0 ? lastStateIdx : (profileStateIdx >= 0 ? profileStateIdx : 0)] ?? null);
      return;
    }
    if (!selectedYear && yearOptions.length > 0) {
      // Sticky/profile year wins only if it actually maps to subjects. Otherwise the cascade
      // collapses and the user sees a half-empty modal (no Subject/Task/Response fields).
      const findValidIdx = (target) => {
        if (target == null || target === '') return -1;
        const idx = yearOptions.findIndex((y) => yearMatch(y.title, target) || y.title === `Year ${target}`);
        return idx >= 0 && subjectsAvailableForYear(yearOptions[idx].title) ? idx : -1;
      };
      const lastYearIdx = findValidIdx(last?.year);
      const profileYearIdx = findValidIdx(currentYear);
      const firstValidIdx = yearOptions.findIndex((y) => subjectsAvailableForYear(y.title));
      const chosenIdx = lastYearIdx >= 0
        ? lastYearIdx
        : (profileYearIdx >= 0 ? profileYearIdx : (firstValidIdx >= 0 ? firstValidIdx : 0));
      setSelectedYear(yearOptions[chosenIdx] ?? null);
      return;
    }
    if (!disambiguatedSubjectOptions.length) return;
    if (!selectedSubject && disambiguatedSubjectOptions.length > 0) {
      const lastSubjIdx = last?.subjectCode ? disambiguatedSubjectOptions.findIndex((s) => s.code === last.subjectCode) : -1;
      setSelectedSubject(disambiguatedSubjectOptions[lastSubjIdx >= 0 ? lastSubjIdx : 0]);
      return;
    }
    if (!selectedTaskType && taskTypeOptions.length > 0) {
      // Alias-aware so a stored legacy value maps onto its canonical option.
      const lastTaskIdx = last?.taskType ? taskTypeOptions.findIndex((t) => matchesTypeOption(t, last.taskType)) : -1;
      setSelectedTaskType(taskTypeOptions[lastTaskIdx >= 0 ? lastTaskIdx : 0]);
      return;
    }
    if (!selectedResponseType && responseTypeOptions.length > 0) {
      const lastRespIdx = last?.responseType ? responseTypeOptions.findIndex((r) => matchesTypeOption(r, last.responseType)) : -1;
      setSelectedResponseType(responseTypeOptions[lastRespIdx >= 0 ? lastRespIdx : 0]);
      return;
    }

    // All selections complete. A list can be legitimately empty (e.g. no response
    // types for a Short Answer task type), so treat "no options to pick from" as
    // satisfied — otherwise initialization never finishes and the post-init sync
    // effects stay disabled for the life of the popup.
    const stateDone = !!selectedState || stateOptions.length === 0;
    const yearDone = !!selectedYear || yearOptions.length === 0;
    const taskDone = !!selectedTaskType || taskTypeOptions.length === 0;
    const responseDone = !!selectedResponseType || isShortAnswerTaskSelected || responseTypeOptions.length === 0;
    if (selectedSubject && stateDone && yearDone && taskDone && responseDone) {
      initializedRef.current = true;
    }
  }, [
    open, isCreateMode,
    disambiguatedSubjectOptions, yearOptions, taskTypeOptions, responseTypeOptions,
    stateOptions, isLoadingProfile, loadingSubjects, loadingYears, loadingStates, loadingTaskTypes, loadingResponseTypes,
    selectedSubject, selectedState, selectedYear, selectedTaskType, selectedResponseType,
    currentYear, currentState,
  ]);

  const findSubjectIndex = () => {
    if (selectedSubject) {
      let idx = disambiguatedSubjectOptions.findIndex((s) => s.code === selectedSubject.code || s.title === selectedSubject.title);
      if (idx >= 0) return idx;
    }
    if (isCreateMode) return disambiguatedSubjectOptions.length > 0 ? 0 : -1;
    const submissionCode = getSubmissionSubjectCode();
    let idx = disambiguatedSubjectOptions.findIndex((s) => s.code === submissionCode);
    if (idx >= 0) return idx;
    const subjects = firstQuestion?.subjects ?? submission?.assignment?.subjects;
    let subjectNameToMatch = null;
    if (Array.isArray(subjects) && subjects.length > 0) {
      const firstSubject = subjects[0];
      subjectNameToMatch = typeof firstSubject === 'object'
        ? (firstSubject.subjectName ?? firstSubject.name ?? firstSubject.title)
        : String(firstSubject);
    }
    const currentSubject = subjectNameToMatch || getSubmissionSubject();
    idx = disambiguatedSubjectOptions.findIndex((s) => s.title === currentSubject);
    if (idx < 0 && currentSubject) {
      idx = disambiguatedSubjectOptions.findIndex((s) => s.title.toLowerCase() === currentSubject.toLowerCase());
    }
    return idx;
  };

  const findYearIndex = () => {
    if (selectedYear) {
      const idx = yearOptions.findIndex((y) => y.title === selectedYear.title || yearMatch(y.title, selectedYear.title));
      if (idx >= 0) return idx;
    }
    if (isCreateMode) {
      return yearOptions.findIndex((y) => yearMatch(y.title, currentYear) || y.title === `Year ${currentYear}`);
    }
    const currentYearVal = getSubmissionYear();
    // Try exact match first, then try with dash/space variations
    return yearOptions.findIndex((y) => {
      if (y.title === currentYearVal || yearMatch(y.title, currentYearVal)) return true;
      // Normalize both for comparison - remove dash/space and 'Year' prefix
      const normalizeYear = (yearStr) => {
        if (!yearStr) return '';
        return yearStr.toString().toLowerCase().replace(/year[\s-]*/i, '');
      };
      return normalizeYear(y.title) === normalizeYear(currentYearVal);
    });
  };

  const findStateIndex = () => {
    if (selectedState) {
      const idx = stateOptions.findIndex((s) => s.title === selectedState.title);
      if (idx >= 0) return idx;
    }
    if (isCreateMode) return stateOptions.findIndex((s) => s.title === String(resolvedStateOption?.title ?? currentState));
    return stateOptions.findIndex((s) => s.title === String(getSubmissionState()));
  };

  // Alias-aware match so a stored legacy value (e.g. LONG_ANSWER) selects the existing
  // canonical option (e.g. "Extended Response") rather than failing the raw equality check.
  const responseTypeMatches = (option, candidate) => {
    if (option.value === candidate || option.title === candidate) return true;
    if (normalizeType(option.value) === normalizeType(candidate) || normalizeType(option.title) === normalizeType(candidate)) return true;
    if (isExtendedResponseType(candidate) && (isExtendedResponseType(option.value) || isExtendedResponseType(option.title))) return true;
    if (isShortAnswerType(candidate) && (isShortAnswerType(option.value) || isShortAnswerType(option.title))) return true;
    return false;
  };

  const findResponseTypeIndex = () => {
    if (selectedResponseType) {
      const idx = responseTypeOptions.findIndex(
        (r) => responseTypeMatches(r, selectedResponseType.value) || responseTypeMatches(r, selectedResponseType.title)
      );
      if (idx >= 0) return idx;
    }
    if (isCreateMode) return -1;
    const current = getSubmissionResponseType();
    return responseTypeOptions.findIndex((r) => responseTypeMatches(r, current));
  };

  const findTaskTypeIndex = () => {
    if (selectedTaskType) {
      const selectedValue = selectedTaskType.value || selectedTaskType.title;
      const idx = taskTypeOptions.findIndex(
        (t) => responseTypeMatches(t, selectedValue)
      );
      if (idx >= 0) return idx;
    }
    if (isCreateMode) return -1;
    const currentTaskType = getSubmissionTaskType();
    return taskTypeOptions.findIndex(
      (t) => responseTypeMatches(t, currentTaskType)
    );
  };

  const handleMarksChange = (value) => {
    hasUserChangedMarksRef.current = true;
    setMarksOutOf(value);
  };

  const setSubmittingState = (value) => {
    setIsSubmitting(value);
    onSubmittingChange?.(value);
  };

  // Numeric totalMarks for the API. The field is optional — a blank or invalid entry
  // omits totalMarks rather than failing validation.
  const getTotalMarksPayload = () => {
    if (!isShortAnswerSelected || isNswDoe) return undefined;
    return parseEnteredMarks() ?? undefined;
  };

  const handleCreate = async () => {
    if (onCreateDraft) {
      setSubmittingState(true);
      const subjectObj = selectedSubject || disambiguatedSubjectOptions[0];
      // Look up original (non-disambiguated) title for API
      const originalSubject = subjectOptions.find((s) => s.code === subjectObj?.code) || subjectObj;
      const yearIndex = findYearIndex();
      const displayedYearOption = yearIndex >= 0 ? yearOptions[yearIndex] : yearOptions[0];
      const rawYear = selectedYear?.title ?? displayedYearOption?.title;
      const yearVal = rawYear ? rawYear.replace(/^Year-?/, '').trim() : null;
      const state = selectedState?.title ?? resolvedStateOption?.title ?? currentState ?? '';
      // JED-1025/JED-1004: a Short Answer task has no Response Type — don't carry
      // a (possibly stale) extended-response value into the new draft.
      const responseType = isShortAnswerTaskSelected
        ? ''
        : (selectedResponseType?.value ?? selectedResponseType?.title ?? '');
      const draftSettings = {
        subjectName: originalSubject?.title || '',
        subjectCode: subjectObj?.code || '',
        taskType: selectedTaskType?.value ?? selectedTaskType?.title ?? '',
        year: yearVal,
        state,
        responseType,
        ...(!isNswDoe && isShortAnswerSelected
          ? { totalMarks: getTotalMarksPayload() }
          : {}),
      };
      await onCreateDraft(draftSettings);
      saveLastDraftSettings(draftSettings);
      if (yearVal) {
        profileStateYear({ year: yearVal }).then(() => resetProfile?.()).catch(() => {});
      }
      setSubmittingState(false);
    }
    close();
  };

  const payloadStr = (v) => (v != null && typeof v !== 'object' ? String(v) : toStr(v) || '');

  const handleUpdate = async () => {
    if (isCatalogCascadeSettling) return;
    let patchedSubmission = null;
    let shouldClose = false;
    try {
      setSubmittingState(true);
      const stateVal = payloadStr(selectedState?.title ?? getSubmissionState());
      const subjectObj =
        selectedSubject ||
        disambiguatedSubjectOptions.find(
          (s) => s.code === getSubmissionSubjectCode()
        ) ||
        disambiguatedSubjectOptions[0];
      // Look up original (non-disambiguated) title for API
      const originalSubject =
        subjectOptions.find((s) => s.code === subjectObj?.code) || subjectObj;
      const subject = originalSubject?.title ?? getSubmissionSubject() ?? '';
      const subjectCode = subjectObj?.code ?? '';
      const taskSelVal = selectedTaskType?.value ?? selectedTaskType?.title;
      const taskInList =
        taskSelVal &&
        taskTypeOptions.some((t) => (t.value ?? t.title) === taskSelVal);
      const firstTaskType =
        taskTypeOptions[0]?.value ?? taskTypeOptions[0]?.title ?? '';
      const taskType = payloadStr(
        taskInList
          ? taskSelVal
          : hasCatalogContextChanged
          ? firstTaskType
          : getSubmissionTaskType() ?? ''
      );
      const yearDisplay =
        selectedYear?.title ?? getSubmissionYear() ?? `Year ${currentYear}`;
      const yearVal =
        (payloadStr(yearDisplay) || payloadStr(`Year ${profile?.year ?? '12'}`))
          .replace(/^(?:Year|Grade)\s*-?\s*(\d+)$/i, '$1')
          .trim();
      const respSelVal =
        selectedResponseType?.value ?? selectedResponseType?.title;
      const respInList =
        respSelVal &&
        responseTypeOptions.some((r) => (r.value ?? r.title) === respSelVal);
      const firstResponseType =
        responseTypeOptions[0]?.value ?? responseTypeOptions[0]?.title ?? '';
      // Task/subject/state/year changes invalidate the old response. If the sync
      // effect has not committed its default yet, use only the first option from
      // the current catalog (or clear it when the new task declares none).
      const responseType = isShortAnswerTaskSelected
        ? ''
        : payloadStr(
            respInList
              ? respSelVal
              : hasCatalogContextChanged
              ? firstResponseType
              : getSubmissionResponseType() ?? ''
          );

      const totalMarks = getTotalMarksPayload();
      const wasShortAnswer =
        isShortAnswerType(getSubmissionTaskType()) ||
        isShortAnswerType(getSubmissionResponseType());
      // The subject-settings endpoint distinguishes explicit cleanup from a
      // missing mark. NSW DoE always enforces the no-marks policy; other users
      // clear only when leaving either catalog shape of Short Answer.
      const shouldClearMarks =
        isNswDoe || (wasShortAnswer && !isShortAnswerSelected);
      const settings = {
        subjects:
          subject && subjectCode
            ? [{ subjectCode, subjectName: subject }]
            : undefined,
        year: yearVal,
        years: yearVal ? [yearVal] : undefined,
        states: stateVal ? [stateVal] : undefined,
        taskType,
        responseType,
        ...(!isNswDoe && isShortAnswerSelected ? { totalMarks } : {}),
        ...(shouldClearMarks ? { clearMarks: true } : {}),
      };
      const hasChanges =
        subject !== '' ||
        taskType !== '' ||
        responseType !== '' ||
        yearVal !== '' ||
        stateVal !== '' ||
        totalMarks != null;

      if (hasChanges) {
        patchedSubmission = saveSettings
          ? await saveSettings(settings)
          : await updateSubjectSettings(submission.id, settings);
        setSubmission(() => patchedSubmission);
        onSettingsUpdated?.({ subjectName: subject, subjectCode });
        const yearNum = yearVal
          ? yearVal.replace(/^Year-?\s*/i, '').trim()
          : null;
        // "Last selection" includes edits, not just creates: the next new draft
        // should prefill whatever the user last configured here. Same shape as
        // the create-mode save (year is the bare number string).
        saveLastDraftSettings({
          subjectName: subject,
          subjectCode,
          taskType,
          year: yearNum,
          state: stateVal,
          responseType,
          totalMarks,
        });
        if (yearNum) {
          profileStateYear({ year: yearNum })
            .then(() => resetProfile?.())
            .catch(() => {});
        }
        toast(<Toast message={'Settings updated'} />);
        shouldClose = true;
      } else {
        toast(<Toast message={'No changes to update'} />);
        shouldClose = true;
      }
    } catch (error) {
      // The PATCH response is authoritative. If a later local side effect
      // fails, retain it so editor writes cannot restore pre-PATCH metadata.
      if (patchedSubmission) setSubmission(() => patchedSubmission);
      toast(<Toast message={error.message || 'Failed to update settings'} />);
    } finally {
      setSubmittingState(false);
      if (shouldClose) close();
    }
  };

  const isLoading =
    isLoadingProfile ||
    (isCreateMode
      ? loadingSubjects
      : loadingSubjectsByEffective || loadingSubjectsByState) ||
    loadingTaskTypes ||
    loadingResponseTypes ||
    loadingYears ||
    loadingStates;

  if (!open) return null;

  const title = isCreateMode ? 'Customise your draft' : 'Update your draft settings';
  const buttonText = isCreateMode ? 'Create Draft' : 'Update Draft';

  return (
    <>
      <Overlay onClick={hideClose || isSubmitting ? undefined : close} />
      <ModalContainer
        $create={isCreateMode}
        role="dialog"
        aria-modal="true"
        aria-label="Draft settings"
      >
        {isCreateMode && <SettingsBackground aria-hidden="true" />}
        {!hideClose && (
          <CloseButton
            onClick={close}
            disabled={isSubmitting}
            aria-label="Close draft settings"
          >
            ×
          </CloseButton>
        )}

        <TitleWrapper>
          <Title>{title}</Title>
        </TitleWrapper>
        <Subtitle>
          Your feedback will be generated based on your draft settings
        </Subtitle>
        {showCompact && !isEditingStateYear && (
          <StateYearSummary>
            <StateYearText>
              {showCompactStateYear && <>State: <StateYearValue>{effectiveState}</StateYearValue> | </>}
              Year: <StateYearValue>{effectiveYear}</StateYearValue>
            </StateYearText>
            <EditStateYearButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditingStateYear((prev) => !prev);
              }}
            >
              <img src={EditPurpleIcon} alt="Edit state and year" />
            </EditStateYearButton>
          </StateYearSummary>
        )}

        {isLoading ? (
          <FormContainer>
            <Loader />
          </FormContainer>
        ) : (
          <FormContainer>
            {isCreateMode && hideState && !isNzUser && effectiveState && (
              <FieldGroup>
                <FieldLabel htmlFor="draft-fixed-state">State</FieldLabel>
                <FixedState id="draft-fixed-state" value={effectiveState} readOnly aria-readonly="true" />
              </FieldGroup>
            )}
            {!hideState && (!showCompact || isEditingStateYear) && stateOptions.length > 0 && (
              <FieldGroup>
                <FieldLabel>State</FieldLabel>
                <StyledDropDown
                  menuItems={stateOptions}
                  fullWidth={true}
                  selectedIndex={Math.max(0, findStateIndex())}
                  onItemSelected={(item) => {
                    hasUserChangedStateRef.current = true;
                    setSelectedState(item);
                    // State sits at the top of the cascade. Null everything downstream so
                    // the sync effects pick valid defaults for the new state without racing
                    // against stale values.
                    setSelectedYear(null);
                    setSelectedSubject(null);
                    setSelectedTaskType(null);
                    setSelectedResponseType(null);
                  }}
                  borderRadius={6}
                />
              </FieldGroup>
            )}

            {disambiguatedSubjectOptions.length > 0 && (
              <FieldGroup>
                <FieldLabel>Subject</FieldLabel>
                <StyledDropDown
                  menuItems={disambiguatedSubjectOptions}
                  fullWidth={true}
                  selectedIndex={Math.max(0, findSubjectIndex())}
                  onItemSelected={(item) => {
                    setSelectedSubject(item);
                    hasUserChangedSubjectRef.current = true;
                    setSelectedTaskType(null);
                    setSelectedResponseType(null);
                  }}
                  borderRadius={6}
                />
              </FieldGroup>
            )}

            {(!showCompact || isEditingStateYear) && yearOptions.length > 0 && (
              <FieldGroup>
                <FieldLabel>{lexiconT('Year', profile?.country)}</FieldLabel>
                <StyledDropDown
                  menuItems={yearOptions}
                  fullWidth={true}
                  selectedIndex={Math.max(0, findYearIndex())}
                  onItemSelected={(item) => {
                    hasUserChangedYearRef.current = true;
                    setSelectedYear(item);
                    // Year change can invalidate subject (years coverage) and therefore task
                    // and response types. Null them so sync effects re-pick from fresh options.
                    setSelectedSubject(null);
                    setSelectedTaskType(null);
                    setSelectedResponseType(null);
                  }}
                  borderRadius={6}
                />
              </FieldGroup>
            )}

            {taskTypeOptions.length > 0 && (
              <FieldGroup>
                <FieldLabel>Task Type</FieldLabel>
                <StyledDropDown
                  menuItems={taskTypeOptions}
                  fullWidth={true}
                  selectedIndex={Math.max(0, findTaskTypeIndex())}
                  onItemSelected={(item) => {
                    hasUserChangedTaskTypeRef.current = true;
                    setSelectedTaskType(item);
                    setSelectedResponseType(null);
                  }}
                  borderRadius={6}
                />
              </FieldGroup>
            )}

            {responseTypeOptions.length > 0 && !isShortAnswerTaskSelected && (
              <FieldGroup>
                <FieldLabel>Response type</FieldLabel>
                <StyledDropDown
                  menuItems={responseTypeOptions}
                  fullWidth={true}
                  selectedIndex={Math.max(0, findResponseTypeIndex())}
                  onItemSelected={(item) => setSelectedResponseType(item)}
                  borderRadius={6}
                />
              </FieldGroup>
            )}

            {isShortAnswerSelected && !isNswDoe && (
              <ShowMarksControl
                value={marksOutOf}
                onValueChange={handleMarksChange}
                warning={marksWarning}
              />
            )}
          </FormContainer>
        )}

        {!isLoading && (
          <ButtonFooterWrapper>
            <SubmitButton
              onClick={isCreateMode ? handleCreate : handleUpdate}
              disabled={
                isSubmitting ||
                isCatalogCascadeSettling ||
                isMarksRequiredAndMissing()
              }
            >
              {isSubmitting ? 'Saving...' : buttonText}
            </SubmitButton>

          </ButtonFooterWrapper>
        )}
      </ModalContainer>
    </>
  );
};


const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 1000;
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  box-sizing: border-box;
  width: 520px !important;
  min-width: 520px;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  background: #FFFFFF;
  border-radius: 16px;
  padding: 40px 48px 48px;
  z-index: 1001;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);

  ${(props) => props.$create && `
    width: 747px !important;
    min-width: 0;
    max-width: calc(100vw - 32px);
    padding: 60px 30px;
    border: 1px solid #d3d3d3;
    border-radius: 12px;
    box-shadow: 0 4px 44.6px rgba(140, 104, 174, .22);
    overflow-x: hidden;
    & > :not([aria-hidden]) { position: relative; z-index: 1; }
    & > button[aria-label="Close draft settings"] { position: absolute; top: 19px; right: 20px; }
    & h2 { font-size: 32px; font-weight: 700; letter-spacing: -.32px; }
    & > p { font-size: 16px; line-height: 20px; margin: 8px auto 40px; max-width: 449px; }
    & > div:not([aria-hidden]) { max-width: 475px; margin-left: auto; margin-right: auto; }
    & > div:first-of-type { margin-bottom: 8px; }
    & label { font-size: 16px; color: #000; line-height: 21px; }
    & .MuiInputBase-root { height: 48px; border-radius: 8px; }
    & .MuiSelect-select { padding: 12px; }
  `}

  @media (max-width: 765px) {
    width: calc(100vw - 24px) !important;
    min-width: 0;
    max-width: calc(100vw - 24px);
    max-height: calc(100dvh - 24px);
    padding: 32px 20px 28px;
  }

`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  color: #6B7280;
  cursor: pointer;
  line-height: 1;
  padding: 4px;

  &:hover {
    color: #374151;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  text-align: center;
  margin: 0;
`;

const Subtitle = styled.p`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  color: #6B7280;
  text-align: center;
  margin: 15px 15px 24px 15px;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const StateYearSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: -6px 0 20px;
`;

const StateYearText = styled.p`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #6B7280;
  margin: 0;
`;

const StateYearValue = styled.span`
  font-weight: 500;
  color: #7B7382;
`;

const EditStateYearButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(124, 58, 237, 0.1);
    border-radius: 4px;
  }

  img {
    width: 16px;
    height: 16px;
    pointer-events: none;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FieldLabel = styled.label`
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const ButtonFooterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  line-height: 24px;
  background-color: #7200E0;
  color: #FFFFFF;
  border: none;
  border-radius: 24px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #5B00B5;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export default PopupDialogueBox;

const SettingsBackground = styled.div`
  position: absolute;
  top: 437px;
  left: -24px;
  width: 806px;
  height: 602px;
  opacity: .06;
  pointer-events: none;
  background: url(${DraftSettingsBackground}) center / contain no-repeat;
  transform: rotate(.21deg);
`;

const FixedState = styled.input`
  width: 100%;
  height: 48px;
  padding: 12px;
  box-sizing: border-box;
  border: 1px solid #b2aeb7;
  border-radius: 8px;
  background: white;
  color: #4b464f;
  font-family: var(--font-family-ibm_plex_sans);
  font-size: 16px;
`;
